import { useCallback, useEffect, useRef, useState } from "react"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { HttpErrors } from "@cloudbreakus/network-utilities"
import { MULTI_PATIENT_ENABLED } from "constants/featureFlags"
import { LISTEN } from "constants/callTypes"
import { useMultiPatientRefreshInterval } from "contexts/Call/Form/hooks/useMultiPatientRefreshInterval"
import { SSE } from "utils/SSE"
import { useAuth } from "contexts/Auth"
import { useCall } from "contexts/Call"
import { useProfile } from "contexts/Profile"
import { usePureCallback } from "hooks/usePureCallback"
import { dataService } from "utils/dataService"
import { logger } from "utils/logger"
import { toRedacted } from 'martti-agent-services'

export const OUTSOURCE = "OUTSOURCE"
export const OVER_PHONE = "OPI"
export const UNKNOWN = "UNKNOWN"

export const mergeCallSegments = (current, newCs) => {
  return newCs
    ? newCs?.map((cs) => {
      const currentElement = current.find((curr) => curr.id === cs.id) || {}
      return {
        ...currentElement,
        ...cs,
        notes: cs?.notes || currentElement?.notes,
        // Same trap as notes: the notification parser defaults fullName to "", which would
        // otherwise blank a name the API supplied. Anything keyed on the name then reads that
        // interpreter as gone.
        interpreterName: cs?.interpreterName || currentElement?.interpreterName,
        agentId: cs?.agentId ?? currentElement?.agentId,
        startTime: cs?.startTime ?? currentElement?.startTime,
        endTime: cs?.endTime ?? currentElement?.endTime,
      }
    })
    : current
}

export const useCallSegments = () => {
  const { userData } = useAuth()
  const { answeredCall, expectingAnswer } = useCall()
  const { additionalUserData } = useProfile()

  const [callSegments, setCallSegments] = useState([])
  // An empty list reads the same as a call nobody has joined, so consumers that decide
  // ownership from it need to know whether a list has actually been read yet.
  const [segmentsLoaded, setSegmentsLoaded] = useState(false)
  const segmentReads = useRef({ requested: 0, applied: 0 })
  const subscriptionIds = useRef(null)

  const multiPatientEnabled = useConfigFlag(MULTI_PATIENT_ENABLED)
  const refreshIntervalSeconds = useMultiPatientRefreshInterval({ listeningIn: answeredCall?._action === LISTEN })

  const showSegment = useCallback(
    (cs) => {
      if ((cs.type || cs.callSegmentType) === UNKNOWN) {
        return false
      }
      if (cs.participantMode === "LISTEN" && userData.role !== "SUPERVISOR") {
        return false
      }
      const inHouseUser = !additionalUserData?.microCallCenter
      const inHouseSegment = !cs?.interpreter?.external && !cs?.agentMicroCallCenter
      if (inHouseUser === inHouseSegment) {
        return true
      }
      if ([OVER_PHONE, OUTSOURCE].includes(cs.type || cs.callSegmentType)) {
        return true
      }
    },
    [userData?.role, additionalUserData?.microCallCenter]
  )

  const parseAPICallSegments = useCallback(
    (response) => {
      return response?.filter?.(showSegment)?.map((cs) => {
        const {
          id,
          callId,
          name,
          notes,
          languageId,
          callSegmentType,
          agentId,
          channel,
          agentName = "",
          interpreterName = "",
          outsourcePartnerName,
          startTime,
          endTime,
        } = cs
        return {
          id,
          callId,
          name,
          notes,
          languageId,
          type: callSegmentType,
          partnerName: outsourcePartnerName,
          interpreterId: agentId || additionalUserData?.id,
          interpreterName: agentName || interpreterName,
          channel,
          agentId,
          startTime,
          endTime,
        }
      })
    },
    [showSegment, additionalUserData]
  )

  const onAPICallSegmentsReceived = usePureCallback((newSegments) => {
    const parsed = parseAPICallSegments(newSegments)
    setCallSegments((current) => mergeCallSegments(current, parsed))
    setSegmentsLoaded(true)
  })

  const fetchAPICallSegments = usePureCallback(async (connected, preserveWhenEmpty = false) => {
    if (!connected || !answeredCall?.interaction) {
      return null
    }
    const reads = segmentReads.current
    const readVersion = ++reads.requested
    try {
      const response = await dataService.Interactions.CallSegments.get({ interactionId: answeredCall.interaction.id })
      logger.debug("Call Segments fetched", response?.data?.map(toRedacted))
      // A pending newer poll must not starve successful reads on a slow API. Only a
      // newer applied response (or interaction cleanup) makes this response stale.
      if (readVersion <= reads.applied || (preserveWhenEmpty && !response?.data?.length)) return
      reads.applied = readVersion
      onAPICallSegmentsReceived(response?.data)
    } catch (err) {
      if (err instanceof HttpErrors.ThrottledNetworkError) {
        logger.debug("Call segments read cancelled for a newer read", err)
        return
      }
      logger.error("Unable to fetch call segments", err)
    }
  })

  const parseSSECallSegments = useCallback(
    (callSegments) => {
      return callSegments?.filter?.(showSegment).map((cs) => {
        const {
          callSegmentId,
          callSegmentName,
          notes,
          language,
          type,
          channel,
          interpreter,
          fullName = "",
          interaction,
          outsourcePartner,
        } = cs
        return {
          id: callSegmentId,
          callId: interaction?.interactionId,
          name: callSegmentName,
          notes,
          languageId: language?.languageId,
          type,
          partnerName: outsourcePartner?.name,
          interpreterId: interpreter?.interpreterId || additionalUserData?.id,
          interpreterName: fullName,
          channel: channel,
          agentId: interpreter?.interpreterId,
          startTime: cs.startTime,
          endTime: cs.endTime,
        }
      })
    },
    [showSegment, additionalUserData]
  )

  // The subscription carries only presence and timing -- no name, no notes. Keys it has no
  // value for are left off entirely so mergeCallSegments keeps what the API read supplied.
  const parseSubscribedCallSegments = useCallback(
    (segments) =>
      (segments ?? []).map((cs) => ({
        id: cs.id,
        agentId: cs.userId,
        interpreterId: cs.userId || additionalUserData?.id,
        startTime: cs.startTime,
        endTime: cs.endTime,
      })),
    [additionalUserData]
  )

  const onSubscribedCallSegmentsReceived = usePureCallback((segments) => {
    if (!segments) return
    const updates = new Map(parseSubscribedCallSegments(segments).map((cs) => [cs.id, cs]))
    // This stream only contains inhouse IDs and timing. It cannot decide visibility and is not
    // a complete list: adding its unknown rows exposes LISTEN segments, while replacing the
    // list removes OPI/outsource segments. Only the API/SSE lists carry the classification.
    setCallSegments((current) => mergeCallSegments(current, current.map((cs) => updates.get(cs.id) || cs)))

    const ids = segments.map((cs) => cs.id).sort().join(",")
    const changed = subscriptionIds.current !== ids
    subscriptionIds.current = ids
    if (changed && segments.some((cs) => !callSegments.some((current) => current.id === cs.id))) {
      fetchAPICallSegments(true)
    }
  })

  const onSSECallSegmentsReceived = usePureCallback((newSegments) => {
    const parsed = parseSSECallSegments(newSegments)
    setCallSegments((current) => mergeCallSegments(current, parsed))
  })

  useEffect(() => {
    if (!answeredCall?.interaction?.id) {
      return
    }

    const reads = segmentReads.current
    fetchAPICallSegments(true)

    SSE.on("call-segments", onSSECallSegmentsReceived)
    SSE.on("connect", fetchAPICallSegments)
    SSE.subscribe("call-segments", answeredCall?.interaction?.id)
    return () => {
      SSE.off("call-segments", onSSECallSegmentsReceived)
      SSE.unsubscribe("call-segments")
      SSE.off("connect", fetchAPICallSegments)
      reads.applied = ++reads.requested
      subscriptionIds.current = null
      refetchedForIdentity.current = null
      setCallSegments([])
      setSegmentsLoaded(false)
    }
  }, [answeredCall?.interaction?.id, onSSECallSegmentsReceived, fetchAPICallSegments, onAPICallSegmentsReceived])

  // Departures have no push otherwise: the SSE payload has no endTime, so an interpreter who
  // hangs up looks present until the list is read again. Subscribing means the others notice
  // within about a second instead of on the next poll, which still runs as a fallback.
  useEffect(() => {
    const interactionId = answeredCall?.interaction?.id
    if (!interactionId || !multiPatientEnabled) return undefined

    const onError = (err) => {
      logger.error(`Call segment subscription failed for interaction ${interactionId}`, err)
    }

    const unsubscribe = dataService.Interactions.CallSegments.subscribe(onSubscribedCallSegmentsReceived, {
      interactionId,
      onError,
    })

    return () => {
      if (Array.isArray(unsubscribe)) {
        unsubscribe.forEach((fn) => fn?.())
        return
      }
      unsubscribe?.()
    }
  }, [answeredCall?.interaction?.id, multiPatientEnabled, onSubscribedCallSegmentsReceived])

  const refetchedForIdentity = useRef(null)

  // Nothing else tells a session that somebody left. The notification payload carries no
  // endTime, so an interpreter who hangs up leaves their segment looking open until the list is
  // read again -- which used to happen only at call start, so the interpreter left behind stayed
  // locked out of the patient until they reloaded the page.
  useEffect(() => {
    const intervalMs = Number(refreshIntervalSeconds) * 1000
    if (!answeredCall?.interaction?.id || !multiPatientEnabled) return undefined
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) return undefined

    const timer = setInterval(() => fetchAPICallSegments(true), intervalMs)
    return () => clearInterval(timer)
  }, [answeredCall?.interaction?.id, multiPatientEnabled, refreshIntervalSeconds, fetchAPICallSegments])

  useEffect(() => {
    // PI-469: At the start of calls, there is a race between the media server and the agent client/notification server
    // In some cases, by the time the request to subscribe to the call segments was received by the server, the media
    // server had already dispatched events for the first participants joining, therefore the agent client would miss
    // the call segment updated events triggered by those media server events.
    // In that case the call sheet is stuck with no call segments to display at the start of the call.
    // Here we use the expectingAnswer value from the call context, which changes when the webrtc CALL_STARTED event fires
    // If the call does not have call segments by the time the webrtc call starts, we fetch them again
    const interactionId = answeredCall?.interaction?.id
    // A segment the subscription delivered has an agent but no name -- the subscription does
    // not carry one. Under multi-patient the name is shown to the other interpreters, so a
    // missing one is worth another read; without it nothing displays a name and the extra
    // request would be waste.
    const needsIdentity = (cs) => !cs.agentId || (multiPatientEnabled && !cs.interpreterName)
    const unidentified = callSegments.filter(needsIdentity).map((cs) => cs.id).sort().join(",")
    const needsIdentityRefetch = unidentified.length > 0 && refetchedForIdentity.current !== unidentified

    if (interactionId && expectingAnswer === false && (!callSegments.length || needsIdentityRefetch)) {
      if (needsIdentityRefetch) {
        refetchedForIdentity.current = unidentified
      }
      fetchAPICallSegments(true, true)
    }

  }, [callSegments, expectingAnswer, answeredCall?.interaction?.id, multiPatientEnabled, fetchAPICallSegments]);

  return {
    callSegments,
    segmentsLoaded,
  }
}
